import { useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Header } from './Header';
import { Footer } from './Footer';
import { AuthModal } from '@/components/auth/AuthModal';
import { SessionGuardBanner } from '@/components/auth/SessionGuardBanner';
import { useSessionGuard } from '@/hooks/useSessionGuard';
import { usePublicSettings } from '@/hooks/usePublicSettings';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/lib/constants';

interface PageShellProps {
  children: ReactNode;
  /** Remove the default horizontal padding from <main>. Useful for full-bleed pages. */
  fullBleed?: boolean;
  className?: string;
}

/**
 * Global page shell.
 *
 * Hosts the AuthModal, the SessionGuardBanner, and invokes the
 * useSessionGuard side effect (toast + token clear on supersede).
 *
 * The banner renders only when status === 'superseded', so it's
 * invisible in the common case.
 */
export function PageShell({ children, fullBleed = false, className }: PageShellProps) {
  const [authOpen, setAuthOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Signing in from a public page (landing, terms, privacy) continues to
  // the participant's workshop; authed pages stay where they are.
  const onAuthenticated = () => {
    if ([ROUTES.home, ROUTES.terms, ROUTES.privacy].includes(location.pathname as typeof ROUTES.home)) {
      navigate(ROUTES.projects);
    }
  };

  // Side effect: shows toast + clears local token if superseded.
  useSessionGuard();
  const { maintenanceMessage } = usePublicSettings();

  return (
    <div className={cn('relative flex min-h-screen flex-col', className)}>
      <SessionGuardBanner onSignInClick={() => setAuthOpen(true)} />
      {maintenanceMessage ? (
        <div
          role="status"
          className="relative z-20 border-b border-ochre/40 bg-ochre/10 px-6 py-2 text-center font-mono text-[12px] text-ink"
        >
          <span className="text-ochre">§</span>&nbsp;&nbsp;{maintenanceMessage}
        </div>
      ) : null}
      <Header onSignInClick={() => setAuthOpen(true)} />

      <main
        className={cn(
          'flex-1',
          !fullBleed && 'mx-auto w-full max-w-7xl px-6 py-16',
        )}
      >
        {children}
      </main>

      <Footer />

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} onAuthenticated={onAuthenticated} />
    </div>
  );
}
